import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/** Desktop presentation of the native Shopify facet form. */
class DesktopFacets extends Component {
  #desktop = window.matchMedia('(min-width: 1024px)');
  #listeners;
  #request;
  #revision = 0;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('keydown', this.#key, options);
    document.addEventListener('pointerdown', this.#outside, options);
    window.addEventListener('popstate', this.#history, options);
    window.addEventListener('resize', this.#schedule, options);
    window.addEventListener('scroll', this.#schedule, { ...options, passive: true });
    this.#desktop.addEventListener('change', this.#mode, options);
    this.#mode();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#mode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#revision++;
    cancelAnimationFrame(this.#frame);
  }

  #mode = () => {
    // Hidden native select and custom radios must never submit sort_by twice.
    const legacy = this.closest('results-list')?.querySelector('.plp-legacy-toolbar .sorting-filter__select');
    if (legacy) legacy.disabled = this.#desktop.matches;
    this.querySelectorAll('input[name="sort_by"]').forEach(input => { input.disabled = !this.#desktop.matches; });
    if (!this.#desktop.matches) this.#close();
    const grid = this.closest('results-list')?.querySelector('.product-grid');
    const layout = grid?.getAttribute('product-grid-view');
    const chosen = this.querySelector(`input[name="grid"][value="${layout}"]`);
    if (chosen) chosen.checked = true;
    const selected = this.querySelector('input[name="sort_by"]:checked');
    const label = this.querySelector('[data-sort-label]');
    if (label && selected) label.textContent = selected.closest('label').querySelector('span').textContent;
    this.#schedule();
  };

  #close(except) {
    this.querySelectorAll('.desktop-facet').forEach(details => {
      if (details !== except) details.open = false;
      details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
    });
  }

  #click = event => {
    if (!this.#desktop.matches) return;
    if (event.target.closest('.desktop-facets__retry')) { this.refresh(); return; }
    const summary = event.target.closest('.desktop-facet > summary');
    if (!summary || !this.contains(summary)) return;
    event.preventDefault();
    const details = summary.parentElement;
    details.open = !details.open;
    this.#close(details);
    summary.setAttribute('aria-expanded', String(details.open));
    this.#schedule();
  };

  #change = event => {
    if (!this.#desktop.matches || event.target.name !== 'sort_by') return;
    const details = event.target.closest('details');
    this.#close();
    details.querySelector('summary').focus({ preventScroll: true });
    this.querySelector('facets-form-component').updateFilters();
  };

  #outside = event => {
    if (!this.#desktop.matches) return;
    const open = this.querySelector('.desktop-facet[open]');
    if (open && !open.contains(event.target) && !event.target.closest('.desktop-facet > summary')) this.#close();
  };

  #key = event => {
    if (!this.#desktop.matches) return;
    const details = event.target.closest('.desktop-facet');
    if (!details) return;
    const summary = details.querySelector('summary');
    if (event.key === 'Escape' && details.open) {
      event.preventDefault(); this.#close(); summary.focus({ preventScroll: true }); return;
    }
    // Native radios and checkboxes handle selection; arrows navigate list focus.
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    if (details.classList.contains('desktop-facet--price')) return;
    event.preventDefault();
    details.open = true; this.#close(details);
    summary.setAttribute('aria-expanded', 'true');
    const inputs = [...details.querySelectorAll('input:not(:disabled)')];
    if (!inputs.length) return;
    const index = inputs.indexOf(event.target);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? inputs.length - 1
      : event.key === 'ArrowDown' ? Math.min(index + 1, inputs.length - 1)
      : index < 0 ? inputs.length - 1 : Math.max(index - 1, 0);
    inputs[next].focus({ preventScroll: true });
    inputs[next].closest('label').scrollIntoView({ block: 'nearest' });
    this.#schedule();
  };

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (!this.#desktop.matches) return;
      this.querySelectorAll('.desktop-facet[open]').forEach(details => {
        const rect = details.querySelector('summary').getBoundingClientRect();
        const below = window.innerHeight - rect.bottom - 20;
        const headerBottom = Math.max(0, ...[...document.querySelectorAll('.shopify-section-group-header-group')]
          .map(header => header.getBoundingClientRect().bottom));
        const above = rect.top - headerBottom - 20;
        const up = below < 80 && above > below;
        details.classList.toggle('desktop-facet--up', up);
        details.style.setProperty('--facet-panel-height', `${Math.max(40, Math.min(400, up ? above : below))}px`);
      });
    });
  };

  #capture() {
    const details = this.querySelector('.desktop-facet[open]');
    return {
      key: details?.dataset.filterKey,
      scroll: details?.querySelector('.desktop-facet__panel').scrollTop || 0,
      focus: this.contains(document.activeElement) ? document.activeElement.id : null,
      layout: this.closest('results-list')?.querySelector('.product-grid')?.getAttribute('product-grid-view'),
    };
  }

  #restore(saved) {
    const grid = this.closest('results-list')?.querySelector('.product-grid');
    if (grid && saved?.layout) grid.setAttribute('product-grid-view', saved.layout);
    this.#mode();
    if (!this.#desktop.matches) return;
    this.#close();
    const details = [...this.querySelectorAll('.desktop-facet')].find(d => d.dataset.filterKey === saved?.key);
    if (details) {
      details.open = true;
      details.querySelector('summary').setAttribute('aria-expanded', 'true');
      details.querySelector('.desktop-facet__panel').scrollTop = saved.scroll;
    }
    if (saved?.focus) this.querySelector(`#${CSS.escape(saved.focus)}`)?.focus({ preventScroll: true });
    this.#schedule();
  }

  #history = () => {
    if (this.#desktop.matches) this.refresh();
  };

  async refresh() {
    const revision = ++this.#revision;
    this.#request?.abort();
    const request = this.#request = new AbortController();
    const status = this.querySelector('.desktop-facets__status');
    const retry = this.querySelector('.desktop-facets__retry');
    this.setAttribute('aria-busy', 'true');
    status.textContent = this.dataset.loadingText || '';
    retry.hidden = true;
    const sectionId = this.getAttribute('section-id');
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error(`Facet response ${response.status}`);
      const html = await response.text();
      if (revision !== this.#revision || !this.isConnected) return;
      const saved = this.#capture();
      await morphSection(sectionId, html);
      if (revision !== this.#revision || !this.isConnected) return;
      this.#mode();
      this.#restore(saved);
      document.dispatchEvent(new CustomEvent(ThemeEvents.collectionRerendered));
    } catch (error) {
      if (error.name === 'AbortError' || revision !== this.#revision || !this.isConnected) return;
      this.querySelector('.desktop-facets__status').textContent = this.dataset.errorText;
      this.querySelector('.desktop-facets__retry').hidden = false;
      this.#close();
      this.querySelector('.desktop-facets__retry').focus({ preventScroll: true });
    } finally {
      if (revision === this.#revision && this.isConnected) {
        this.removeAttribute('aria-busy');
        if (this.querySelector('.desktop-facets__retry').hidden) this.querySelector('.desktop-facets__status').textContent = '';
      }
    }
  }
}

if (!customElements.get('desktop-facets-component')) customElements.define('desktop-facets-component', DesktopFacets);
